import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { useAudioPlayerQueue } from "@/components/providers/AudioPlayerProvider/useAudioPlayer";
import { apiClient } from "@/lib/lutely-api/api-request";
import { useApi } from "@/components/providers/ApiProvider/hooks";

interface TrackDetail {
  trackId: number;
  title: string;
  description: string | null;
  type: string;
  url: string | null;
  slug: string;
  creatorId: number;
  fileKey: string | null;
  fileName: string | null;
  metadata: unknown;
}

const CREATOR_SLUG = "qa-demo";
const TRACK_SLUG = "beat-of-the-music";

export const Route = createFileRoute("/dev/_devLayout/audio-test")({
  component: AudioTestRoute,
});

function AudioTestRoute() {
  const { registerTracks, setQueue, clearQueue } = useAudioPlayerQueue();
  const { apiClientConfigured } = useApi();
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current || !apiClientConfigured) return;
    hasLoadedRef.current = true;

    async function loadTrack() {
      setStatus("loading");
      setErrorMessage(null);

      try {
        const response = await apiClient.get<TrackDetail>("/tracks", {
          params: {
            trackSlug: TRACK_SLUG,
            creatorSlug: CREATOR_SLUG,
          },
        });
        const detail = response.data;
        if (!detail.url) {
          throw new Error("Track detail missing streaming URL");
        }

        const libraryTrackId = detail.slug || String(detail.trackId);
        const duplicateTrackId = `${libraryTrackId}-duplicate`;

        registerTracks([
          {
            id: libraryTrackId,
            trackId: String(detail.trackId),
            title: detail.title ?? "Untitled Track",
            creatorId: String(detail.creatorId),
            creator: CREATOR_SLUG,
            src: detail.url,
            durationMs: undefined,
          },
          {
            id: duplicateTrackId,
            trackId: String(detail.trackId),
            title: `${detail.title ?? "Untitled Track"} (Duplicate)`,
            creatorId: String(detail.creatorId),
            creator: CREATOR_SLUG,
            src: detail.url,
            durationMs: undefined,
          },
        ]);

        setQueue({
          trackIds: [libraryTrackId, duplicateTrackId],
          startTrackId: libraryTrackId,
          autoPlay: true,
          startPositionMs: 0,
          replace: true,
        });
        setStatus("ready");
      } catch (error) {
        setStatus("error");
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage("Unknown error loading track");
        }
        clearQueue();
      }
    }

    loadTrack();
  }, [apiClientConfigured, clearQueue, registerTracks, setQueue]);

  const description = apiClientConfigured
    ? "Queues the real API track twice so you can exercise next/previous controls without extra setup."
    : "Waiting for API client to initialize…";

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <h1 className="text-2xl font-semibold">Audio Player Validation</h1>
      <p className="text-sm text-neutral-400">
        {description} Use the persistent player bar at the bottom of the app to
        control playback.
      </p>
      <StatusBlock status={status} errorMessage={errorMessage} />
    </div>
  );
}

function StatusBlock({
  status,
  errorMessage,
}: {
  status: "idle" | "loading" | "ready" | "error";
  errorMessage: string | null;
}) {
  if (status === "ready") {
    return (
      <div className="rounded-md border border-green-500/40 bg-green-500/10 p-4 text-sm text-green-200">
        Track loaded. Use the global player controls to verify playback and
        waveform interaction.
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="rounded-md border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
        Failed to load track: {errorMessage}
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="rounded-md border border-blue-500/40 bg-blue-500/10 p-4 text-sm text-blue-200">
        Loading track detail and queueing player…
      </div>
    );
  }

  return (
    <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-4 text-sm text-yellow-200">
      Waiting to load track…
    </div>
  );
}
